import { css } from '@emotion/react';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import {
  FixedSizeMultiSortable,
  IRenderGroupProps,
} from '../../../components/SortableMultiList/FixedSizeMultiSortable';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { colors } from '../../../theme/colors';
import { StandardFormatterId } from '../../../util/formatter-service';
import { FieldsPerPage } from '../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import { getPageForIndex } from './data-display-utils';
import { fieldsConfigContainerCss, fieldsListContainerCss, fieldItemCss } from './data-display-common-styles';
import { DetailsFieldSelect } from './DetailsFieldSelect';
import { FieldFormatConfig, FormatterSelect } from '../../../components/FormatterSelect/FormatterSelect';

const itemContainer = css`
  ${fieldItemCss};
  grid-template-columns: auto 1fr 1fr auto;
`;
const pageLabel = css`
  background: ${colors.neutral[10]};
  padding: 0.75rem 1rem;
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const separator = css`
  background: ${colors.primary[0]};
  height: 0.5rem;
`;

export interface ReOrderableField {
  label?: string;
  key: string;
  formatConfig?: FieldFormatConfig;
}

export interface IDraggableField extends ReOrderableField {
  index: number;
  id: string;
}

export interface IFieldOrderConfigProps {
  onRemoveField: (field: IDraggableField) => void;
  onReorder: (groupedItems: Record<string, IDraggableField[]>) => void;
  onAddField: (value: string) => void;
  onFormatChange?: (key: string, formatterId: StandardFormatterId | string | undefined) => void;
  selectedFields: string[];
  allFields: ReOrderableField[];
  fieldsPerPage?: number;
  /**
   * How the field groups are labelled.
   *
   * `pages` — "Page 1", "Page 2", … for sections the profile paginates.
   * `visibility` — "Visible" then "Behind Show All", for sections that show one group of fields and
   * put the rest behind a Show All link, where page numbers configure nothing the user sees.
   *
   * Labels only: the groups themselves stay fixed-size either way, because
   * `handleDragOverFixedSizeGroup` redistributes fields across the existing keys in `groupSize`
   * chunks. Unequal groups would drop the overflow on the first drag.
   */
  groupLabelMode?: 'pages' | 'visibility';
}

/**
 * Heading for one field group. In `visibility` mode only the first two groups are labelled — the
 * first is what the profile shows, the second opens the region revealed by "Show All". Later groups
 * are continuations of that region and get a separator but no heading, so the config reads as two
 * regions rather than as pages.
 */
export function getGroupHeading(
  groupName: string,
  mode: 'pages' | 'visibility',
  fieldsPerPage: number
): { label: string | null; caption: string | null } {
  if (mode === 'pages') {
    return { label: groupName, caption: `${fieldsPerPage} fields/page` };
  }
  if (groupName === 'Page 1') {
    return { label: 'Visible', caption: `first ${fieldsPerPage} fields` };
  }
  if (groupName === 'Page 2') {
    return { label: 'Behind “Show All”', caption: null };
  }
  return { label: null, caption: null };
}

export function FieldOrderConfig({
  onAddField,
  onRemoveField,
  onReorder,
  selectedFields,
  allFields,
  fieldsPerPage = FieldsPerPage,
  groupLabelMode = 'pages',
  onFormatChange,
}: IFieldOrderConfigProps) {
  const fieldOptions = useMemo(() => {
    const selectedSet = new Set(selectedFields);
    return allFields.reduce((acc, { label, key }) => {
      if (selectedSet.has(key)) {
        return acc;
      }
      acc.push({
        displayName: label ?? key,
        value: key,
      });
      return acc;
    }, [] as ISimpleChoice<string>[]);
  }, [allFields, selectedFields]);

  const renderGroup = useCallback(
    ({ groupName, children }: IRenderGroupProps) => {
      const isFirstGroup = groupName === 'Page 1';
      const { label, caption } = getGroupHeading(groupName, groupLabelMode, fieldsPerPage);

      return (
        <Stack key={groupName}>
          {!isFirstGroup && <div css={separator} />}
          {label && (
            <div css={pageLabel}>
              <Typography variant='body2' color='text.secondary'>
                {label}
              </Typography>
              {caption && (
                <Typography variant='caption' color='text.secondary'>
                  {caption}
                </Typography>
              )}
            </div>
          )}
          {children}
        </Stack>
      );
    },
    [fieldsPerPage, groupLabelMode]
  );

  const renderItem = useCallback(
    (field: IDraggableField) => {
      const { id, label, key } = field;
      const formatConfig = field.formatConfig;
      return (
        <Stack>
          <SortableItem id={id}>
            <div css={itemContainer}>
              <BasicDragHandle />
              <Typography variant={'body2'} noWrap minWidth={'1rem'}>
                {label ?? key}
              </Typography>
              {formatConfig && onFormatChange && (
                <FormatterSelect
                  fieldKey={key}
                  renderer={formatConfig.renderer}
                  value={formatConfig.formatter}
                  onChange={(formatterId) => onFormatChange(key, formatterId)}
                />
              )}
              <IconButton
                onClick={() => {
                  onRemoveField(field);
                }}
                sx={{ justifySelf: 'end', marginLeft: '0.5rem' }}
              >
                <ClearIcon htmlColor={colors.neutral[50]} fontSize='small' />
              </IconButton>
            </div>
          </SortableItem>
        </Stack>
      );
    },
    [onFormatChange, onRemoveField]
  );

  const fields = useMemo(() => {
    if (!selectedFields) {
      return null;
    }
    const fieldMap = allFields.reduce((acc, field) => {
      return acc.set(field.key, field);
    }, new Map<string, ReOrderableField>());

    // A selected key with no matching field (a deleted KPI, say) is ignored: it takes no slot, so
    // both the pages and each field's `index` count over the fields actually rendered. Counting the
    // pages over `selectedFields` instead would let an ignored key push later fields onto a page of
    // their own.
    const renderedFields = selectedFields.reduce((acc, field) => {
      const formField = fieldMap.get(field);

      if (!formField) {
        return acc;
      }

      acc.push({ ...formField, id: formField.key, index: acc.length });
      return acc;
    }, [] as IDraggableField[]);

    const draggableItems: Record<string, IDraggableField[]> = renderedFields.reduce(
      (acc, field, i) => {
        const page = getPageForIndex(i, fieldsPerPage);
        if (acc[`Page ${page}`]) {
          acc[`Page ${page}`].push(field);
        } else {
          acc[`Page ${page}`] = [field];
        }
        return acc;
      },
      {} as Record<string, IDraggableField[]>
    );

    return (
      <FixedSizeMultiSortable
        items={draggableItems}
        onChange={onReorder}
        renderItem={renderItem}
        renderGroup={renderGroup}
        groupSize={fieldsPerPage}
      />
    );
  }, [allFields, fieldsPerPage, onReorder, renderGroup, renderItem, selectedFields]);

  return (
    <Stack css={fieldsConfigContainerCss} className='fieldOrderConfig'>
      <Typography component='h6' ml={'1rem'}>
        Field Order
      </Typography>
      <Stack css={fieldsListContainerCss}>
        {fields}
        <DetailsFieldSelect options={fieldOptions} onChange={onAddField} />
      </Stack>
    </Stack>
  );
}
